import './AdminPage.css';

import { ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { ApiVersionSummary } from '../api/releases';
import { useAuth } from '../auth/useAuth';
import DataTable from '../components/DataTable';
import SearchableSelect from '../components/SearchableSelect';
import { useMigrationGuides } from '../query/migrationGuides';
import {
  useConfig,
  useDeletePreRelease,
  useImportSpec,
  useJiraReleases,
  useLinkJiraRelease,
  usePublishVersion,
  useRefreshChangelog,
  useVersions,
} from '../query/releases';

const DEFAULT_API_ID = 'skymap';

interface Message {
  text: string;
  ok: boolean;
}

const MessageText = ({ message }: { message: Message }) => (
  <span
    className={`publish-message ${message.ok ? 'publish-message--ok' : 'publish-message--err'}`}
  >
    {message.text}
  </span>
);

export default function AdminPage() {
  const { role, email } = useAuth();

  const versionsQuery = useVersions(DEFAULT_API_ID);
  const jiraReleasesQuery = useJiraReleases(DEFAULT_API_ID);
  const configQuery = useConfig();

  const importMutation = useImportSpec(DEFAULT_API_ID);
  const linkMutation = useLinkJiraRelease(DEFAULT_API_ID);
  const publishMutation = usePublishVersion(DEFAULT_API_ID);
  const deleteMutation = useDeletePreRelease(DEFAULT_API_ID);
  const refreshMutation = useRefreshChangelog(DEFAULT_API_ID);
  const guides = useMigrationGuides(DEFAULT_API_ID);

  const [selectedJiraId, setSelectedJiraId] = useState<Record<string, string>>({});

  // ── Import-from-backend release creation ──────────────────────────────────
  const today = new Date().toISOString().slice(0, 10);
  const [importVersionId, setImportVersionId] = useState(today);
  const [importSourceChoice, setImportSourceChoice] = useState<string | null>(null);

  const importChannels = configQuery.data?.specSources?.[DEFAULT_API_ID] ?? [];
  // Default to production when available, else the first configured channel —
  // derived rather than pushed into state by an effect, so it is correct on the
  // very first render after the config lands.
  const importSource =
    importSourceChoice ??
    (importChannels.includes('production') ? 'production' : (importChannels[0] ?? ''));

  const jiraReleases = jiraReleasesQuery.data ?? [];
  const jiraReleasesError = jiraReleasesQuery.error
    ? String(
        jiraReleasesQuery.error instanceof Error
          ? jiraReleasesQuery.error.message
          : jiraReleasesQuery.error,
      )
    : null;

  const handleImport = (e: React.FormEvent) => {
    e.preventDefault();
    const versionId = importVersionId.trim();
    if (!versionId || !importSource) {
      return;
    }
    importMutation.mutate({ versionId, source: importSource });
  };

  const handleDelete = (versionId: string) => {
    if (
      !window.confirm(
        `Delete the pre-release ${versionId}? This removes its imported specs and ` +
          `cannot be undone. The release will need to be re-imported.`,
      )
    ) {
      return;
    }
    deleteMutation.mutate(versionId);
  };

  /** The last outcome of a per-row mutation, or null while it has not run for this row. */
  const messageFor = (
    mutation: { variables?: unknown; isError: boolean; isSuccess: boolean; error: unknown },
    versionId: string,
    success: () => string,
  ): Message | null => {
    if (mutation.variables !== versionId) {
      return null;
    }
    if (mutation.isError) {
      return { text: String(mutation.error), ok: false };
    }
    return mutation.isSuccess ? { text: success(), ok: true } : null;
  };

  const importMessage: Message | null = importMutation.isError
    ? { text: String(importMutation.error), ok: false }
    : importMutation.isSuccess
      ? {
          text:
            `Imported ${importMutation.data.imported.map((i) => i.specId).join(', ') || 'no'} ` +
            `spec(s) from ${importMutation.data.source} — registering…`,
          ok: true,
        }
      : null;

  // Not memoised: the cell renderers close over per-render mutation state, and
  // rebuilding eight column definitions is cheaper than the dependency list that
  // would be needed to memoise them correctly.
  const columns: ColumnDef<ApiVersionSummary>[] = [
    {
      accessorKey: 'versionId',
      header: 'Version',
      cell: ({ row }) => <span className="releases-version-id">{row.original.versionId}</span>,
    },
    { accessorKey: 'displayName', header: 'Display Name' },
    {
      accessorKey: 'releaseState',
      header: 'State',
      cell: ({ row }) => (
        <>
          <span className={`release-state-badge release-state-badge--${row.original.releaseState}`}>
            {row.original.releaseState === 'pre-release' ? 'Pre-release' : 'Released'}
          </span>
          {row.original.hotfix && (
            <span className="release-state-badge release-state-badge--hotfix">Hotfix</span>
          )}
        </>
      ),
    },
    {
      accessorKey: 'registeredAt',
      header: 'Registered',
      cell: ({ row }) => new Date(row.original.registeredAt).toLocaleDateString(),
    },
    {
      accessorKey: 'publishedAt',
      header: 'Published',
      cell: ({ row }) =>
        row.original.publishedAt ? new Date(row.original.publishedAt).toLocaleDateString() : '—',
    },
    {
      id: 'changelog',
      header: 'Changelog',
      enableSorting: false,
      cell: ({ row }) => {
        const v = row.original;
        const refreshing = refreshMutation.isPending && refreshMutation.variables === v.versionId;
        const message = messageFor(refreshMutation, v.versionId, () => {
          const skipped = refreshMutation.data?.skippedUncategorized ?? 0;
          return (
            `Refreshed — ${refreshMutation.data?.entries.length ?? 0} entries` +
            (skipped > 0 ? ` · ⚠ ${skipped} ticket(s) skipped (no Changelog Category)` : '')
          );
        });

        if (!v.jiraReleaseId) {
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {jiraReleasesError ? (
                <span style={{ fontSize: '0.75rem', color: 'var(--danger-text)' }}>
                  {jiraReleasesError}
                </span>
              ) : (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                  <SearchableSelect
                    ariaLabel={`Jira release for ${v.versionId}`}
                    options={jiraReleases.map((r) => ({
                      value: r.name,
                      label:
                        r.name +
                        (r.released ? ' (released)' : '') +
                        (r.archived ? ' [archived]' : '') +
                        (r.releaseDate ? ` — ${r.releaseDate}` : ''),
                    }))}
                    placeholder="Select Jira release…"
                    value={selectedJiraId[v.versionId] ?? ''}
                    onChange={(jiraReleaseId) =>
                      setSelectedJiraId((prev) => ({ ...prev, [v.versionId]: jiraReleaseId }))
                    }
                  />
                  <button
                    className="btn-primary btn-sm"
                    disabled={
                      !selectedJiraId[v.versionId] ||
                      (linkMutation.isPending && linkMutation.variables?.versionId === v.versionId)
                    }
                    onClick={() =>
                      linkMutation.mutate({
                        versionId: v.versionId,
                        jiraReleaseId: selectedJiraId[v.versionId],
                      })
                    }
                  >
                    {linkMutation.isPending && linkMutation.variables?.versionId === v.versionId
                      ? 'Linking…'
                      : 'Link'}
                  </button>
                </div>
              )}
              {linkMutation.variables?.versionId === v.versionId &&
                (linkMutation.isError ? (
                  <MessageText message={{ text: String(linkMutation.error), ok: false }} />
                ) : linkMutation.isSuccess ? (
                  <MessageText
                    message={{
                      text: `Linked to ${linkMutation.variables.jiraReleaseId}`,
                      ok: true,
                    }}
                  />
                ) : null)}
            </div>
          );
        }

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              {v.jiraReleaseId}
            </span>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                className="btn-primary btn-sm"
                disabled={refreshing}
                onClick={() => refreshMutation.mutate(v.versionId)}
              >
                {refreshing ? 'Refreshing…' : v.changelogS3Key ? 'Re-fetch' : 'Fetch'}
              </button>
              {v.changelogS3Key && (
                <Link
                  className="btn-primary btn-sm"
                  to={`/apis/${v.apiId}/versions/${encodeURIComponent(v.versionId)}/changelog`}
                >
                  View
                </Link>
              )}
              {message && <MessageText message={message} />}
            </div>
          </div>
        );
      },
    },
    {
      id: 'migration',
      header: 'Migration',
      enableSorting: false,
      cell: ({ row }) => {
        const v = row.original;
        if (!v.jiraReleaseId) {
          return <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>—</span>;
        }
        const generating = guides.isGenerating(v.versionId);
        const message = guides.messageFor(v.versionId);
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                className="btn-primary btn-sm"
                disabled={generating}
                onClick={() => guides.generate(v.versionId)}
              >
                {generating ? 'Generating…' : v.migrationGuideS3Key ? 'Regenerate' : 'Generate'}
              </button>
              {v.migrationGuideS3Key && (
                <Link
                  className="btn-primary btn-sm"
                  to={`/apis/${v.apiId}/versions/${encodeURIComponent(v.versionId)}/migration`}
                >
                  View
                </Link>
              )}
            </div>
            {message && <MessageText message={message} />}
          </div>
        );
      },
    },
  ];

  if (role === 'admin') {
    columns.push({
      id: 'actions',
      header: 'Actions',
      enableSorting: false,
      cell: ({ row }) => {
        const v = row.original;
        if (v.releaseState !== 'pre-release') {
          return null;
        }
        const publishing = publishMutation.isPending && publishMutation.variables === v.versionId;
        const deleting = deleteMutation.isPending && deleteMutation.variables === v.versionId;
        const message =
          messageFor(publishMutation, v.versionId, () =>
            publishMutation.data?.hotfix
              ? `Hotfix published (${publishMutation.data.publishedAt})`
              : `Published (${publishMutation.data?.publishedAt})`,
          ) ??
          (deleteMutation.variables === v.versionId && deleteMutation.isError
            ? { text: String(deleteMutation.error), ok: false }
            : null);

        return (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              className="btn-primary btn-sm"
              disabled={publishing || deleting}
              onClick={() => publishMutation.mutate(v.versionId)}
            >
              {publishing ? 'Publishing…' : v.hotfix ? 'Publish hotfix' : 'Publish'}
            </button>
            <button
              className="btn-danger btn-sm"
              disabled={publishing || deleting}
              onClick={() => handleDelete(v.versionId)}
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
            {message && <MessageText message={message} />}
          </div>
        );
      },
    });
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>Admin</h1>
        <p>
          Signed in as <strong>{email}</strong> ({role})
        </p>
        {role === 'admin' && (
          <nav style={{ display: 'flex', gap: 12, marginTop: 8 }}>
            <Link className="btn-primary btn-sm" to="/admin/subscribers">
              Subscribers
            </Link>
            <Link className="btn-primary btn-sm" to="/admin/notifications">
              Notifications
            </Link>
            <Link className="btn-primary btn-sm" to="/admin/users">
              Users
            </Link>
            <Link className="btn-primary btn-sm" to="/admin/errors">
              Error registry
            </Link>
            <Link className="btn-primary btn-sm" to="/admin/audit">
              Audit log
            </Link>
          </nav>
        )}
      </div>

      <section className="releases-section">
        <h2 className="releases-heading">Release Management</h2>

        <form
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'center',
            flexWrap: 'wrap',
            margin: '0 0 24px',
            padding: 16,
            border: '1px solid var(--border-subtle)',
            borderRadius: 8,
          }}
          onSubmit={handleImport}
        >
          <strong style={{ fontSize: 14 }}>New release</strong>
          <label style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            Version&nbsp;
            <input
              placeholder="2026-06-22"
              style={{
                padding: '6px 10px',
                borderRadius: 6,
                border: '1px solid var(--border-strong)',
                fontSize: 13,
              }}
              type="text"
              value={importVersionId}
              onChange={(e) => setImportVersionId(e.target.value)}
            />
          </label>
          <label style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            Source&nbsp;
            <select
              disabled={importChannels.length === 0}
              style={{
                padding: '6px 10px',
                borderRadius: 6,
                border: '1px solid var(--border-strong)',
                fontSize: 13,
                textTransform: 'capitalize',
              }}
              value={importSource}
              onChange={(e) => setImportSourceChoice(e.target.value)}
            >
              {importChannels.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
          <button
            className="btn-primary btn-sm"
            disabled={
              importMutation.isPending || importChannels.length === 0 || !importVersionId.trim()
            }
            type="submit"
          >
            {importMutation.isPending ? 'Importing…' : 'Import specs'}
          </button>
          {importChannels.length === 0 && (
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              No spec sources configured.
            </span>
          )}
          {importMessage && <MessageText message={importMessage} />}
        </form>

        {versionsQuery.isPending && <p className="releases-loading">Loading versions…</p>}
        {versionsQuery.isError && <p className="upload-message error">Could not load versions.</p>}

        {versionsQuery.isSuccess && (
          <DataTable
            className="releases-table"
            columns={columns}
            data={versionsQuery.data}
            emptyMessage="No versions found."
            enableSorting={true}
            getRowId={(v) => `${v.versionId}-${v.releaseState}`}
            initialSorting={[{ id: 'registeredAt', desc: true }]}
          />
        )}
      </section>
    </div>
  );
}
